import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, Eye, FileText, Database, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';

const AdminPage = () => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [folderFilter, setFolderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [databaseFilter, setDatabaseFilter] = useState('');
  const [folders, setFolders] = useState([]);

  // Fetch all articles from storage bucket
  const fetchArticlesFromStorage = async () => {
    try {
      setLoading(true);
      
      // Get all folders in YPN_Mag
      const foldersResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/admin/storage/folders`);
      const foldersData = await foldersResponse.json();
      
      if (foldersData.success) {
        setFolders(foldersData.folders);
        
        // Get all articles from all folders
        const articlesResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/admin/storage/articles`);
        const articlesData = await articlesResponse.json();
        
        if (articlesData.success) {
          setArticles(articlesData.articles);
          setFilteredArticles(articlesData.articles);
        }
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
      alert('Failed to fetch articles from storage');
    } finally {
      setLoading(false);
    }
  };

  // Filter articles based on search and filters
  useEffect(() => {
    let filtered = articles;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(article => 
        article.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.folder.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Folder filter
    if (folderFilter) {
      filtered = filtered.filter(article => article.folder === folderFilter);
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(article => article.status === statusFilter);
    }

    // Database filter
    if (databaseFilter) {
      if (databaseFilter === 'in_database') {
        filtered = filtered.filter(article => article.in_database);
      } else if (databaseFilter === 'not_in_database') {
        filtered = filtered.filter(article => !article.in_database);
      }
    }

    setFilteredArticles(filtered);
  }, [articles, searchTerm, folderFilter, statusFilter, databaseFilter]);

  useEffect(() => {
    fetchArticlesFromStorage();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'extracted':
        return <Database className="h-4 w-4 text-blue-600" />;
      case 'scripts_generated':
        return <FileText className="h-4 w-4 text-purple-600" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'processing':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'processed':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'extracted':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'scripts_generated':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'error':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'processing':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const handleViewArticle = (article) => {
    window.open(article.public_url, '_blank');
  };

  // v6_documents functionality removed - now using migrated v5_documents

  const handleDownloadArticle = (article) => {
    const link = document.createElement('a');
    link.href = article.public_url;
    link.download = article.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading articles from storage...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="mt-2 text-gray-600">Manage all articles from YPN Magazine storage</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles or folders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Folder Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <select
                value={folderFilter}
                onChange={(e) => setFolderFilter(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="">All Folders</option>
                {folders.map(folder => (
                  <option key={folder} value={folder}>{folder}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="">All Statuses</option>
                <option value="not_processed">Not Processed</option>
                <option value="extracted">Extracted</option>
                <option value="scripts_generated">Scripts Generated</option>
                <option value="processed">Fully Processed</option>
                <option value="error">Error</option>
              </select>
            </div>

            {/* Database Filter */}
            <div className="relative">
              <Database className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <select
                value={databaseFilter}
                onChange={(e) => setDatabaseFilter(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="">All Articles</option>
                <option value="in_database">In Database</option>
                <option value="not_in_database">Not in Database</option>
              </select>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600 font-medium">Total Articles</p>
              <p className="text-2xl font-bold text-blue-900">{articles.length}</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg">
              <p className="text-sm text-indigo-600 font-medium">In Database</p>
              <p className="text-2xl font-bold text-indigo-900">
                {articles.filter(a => a.in_database).length}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600 font-medium">Processed</p>
              <p className="text-2xl font-bold text-green-900">
                {articles.filter(a => a.status === 'processed').length}
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-600 font-medium">With Scripts</p>
              <p className="text-2xl font-bold text-purple-900">
                {articles.filter(a => a.status === 'scripts_generated' || a.status === 'processed').length}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 font-medium">Folders</p>
              <p className="text-2xl font-bold text-gray-900">{folders.length}</p>
            </div>
          </div>
        </div>

        {/* Articles Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Article Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Folder
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    In Database
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Link
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredArticles.map((article, index) => (
                  <tr key={`${article.folder}-${article.name}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-red-500 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{article.name}</div>
                          <div className="text-sm text-gray-500">PDF Document</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{article.folder}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(article.status)}
                        <span className={`ml-2 ${getStatusBadge(article.status)}`}>
                          {article.status || 'Not Processed'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {article.in_database ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                            <span className="text-sm text-green-800 font-medium">Yes</span>
                            {article.document_id && (
                              <span className="ml-2 text-xs text-gray-500">ID: {article.document_id}</span>
                            )}
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-500">No</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {article.size ? `${(article.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewArticle(article)}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                      >
                        View PDF
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewArticle(article)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View PDF"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadArticle(article)}
                          className="text-green-600 hover:text-green-900"
                          title="Download PDF"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No articles found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          )}
        </div>

        {/* Results count */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredArticles.length} of {articles.length} articles
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
